// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { sparseItemSchema } from '@/sparse/api/types';
import { z } from 'zod';

/**
 * Schema for a form item using the unified Item Catalog.
 * Used by shipment, fulfillment order, purchase order, and return order forms.
 */
export const itemFormSchema = z.object({
  item: sparseItemSchema.nullable(),
  quantity: z
    .number()
    .min(0, {
      message: 'Quantity cannot be negative',
    })
    .nullable(),
});

/**
 * Type definition for an item form entry.
 */
export type ItemFormEntry = z.infer<typeof itemFormSchema>;

/**
 * Validates an array of items in a form schema's superRefine callback.
 * Checks that at least one item exists and each item has a valid item reference.
 */
export function validateItemFormEntries(
  items: ItemFormEntry[] | undefined,
  ctx: z.RefinementCtx
) {
  if (!items || items.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 1,
      type: 'array',
      inclusive: true,
      message: 'At least one item is required',
      path: ['items'],
    });
    return;
  }

  items.forEach((entry, index) => {
    if (!entry.item) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Item is required',
        path: ['items', index, 'item'],
      });
    }
    if (entry.quantity !== null && entry.quantity < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Quantity cannot be negative',
        path: ['items', index, 'quantity'],
      });
    }
  });
}
